import { Button, H5, InputEmail, InputPassword } from "@sellar-io/components";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Link, useSearchParams } from "react-router";
import { Alert } from "~/components/alert/Alert";
import { AlertType } from "~/components/alert/AlertType";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { firebaseApp } from "~/firebaseApp";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../hooks/useSignupContext.shared";
import {
  isSignInConnectionError,
  signInConnectionMessage,
} from "../../../hooks/signInConnection";
import { getJwtUrl } from "../signup/signupUrl";

const auth = getAuth(firebaseApp);

export function SignInForm({
  showForgottenPassword = true,
  email,
}: {
  showForgottenPassword?: boolean;
  email?: string;
}): ReactElement {
  const [searchParams] = useSearchParams();
  const searchMessage = searchParams.get("error")
    ? { message: "An unknown error has occurred", type: "error" as const }
    : searchParams.get("success")
      ? {
          message: "Your email address has been verified. You can now log in.",
          type: "success" as const,
        }
      : undefined;
  const [message, setMessage] = useState<string | undefined>(
    searchMessage?.message,
  );
  const [messageType, setMessageType] = useState<AlertType | undefined>(
    searchMessage?.type,
  );
  const [previousSearchMessage, setPreviousSearchMessage] =
    useState(searchMessage);
  if (searchMessage?.message !== previousSearchMessage?.message) {
    setPreviousSearchMessage(searchMessage);
    setMessage(searchMessage?.message);
    setMessageType(searchMessage?.type);
  }
  const [loading, setLoading] = useState(false);
  const { setStep } = useSignupContext();
  const navigate = useNavigate();
  const formHook = useForm<{
    email: string;
    password: string;
  }>({
    shouldUseNativeValidation: true,
    defaultValues: {
      email,
    },
  });

  return (
    <>
      {message && messageType && <Alert type={messageType} message={message} />}
      <form
        className={"flex flex-col gap-4"}
        onSubmit={formHook.handleSubmit(async (data) => {
          if (loading) return;
          setLoading(true);
          setMessage("");
          try {
            await signInWithEmailAndPassword(auth, data.email, data.password);
            const continueUrlString = searchParams.get("continue");
            const jwtSearchParam = getJwtUrl(continueUrlString);
            if (jwtSearchParam) {
              navigate(`/jwt?jwt=${jwtSearchParam}`);
            }
          } catch (e: unknown) {
            const code =
              typeof e === "object" && e !== null && "code" in e
                ? e.code
                : undefined;
            setMessageType("error");
            if (isSignInConnectionError(e)) {
              setMessage(signInConnectionMessage);
            } else if (
              code === "auth/user-not-found" ||
              code === "auth/wrong-password"
            ) {
              setMessage(`The account information provided was not correct.`);
            } else {
              setMessage(
                e instanceof Error
                  ? e.message
                  : "Sign-in was unsuccessful. Please try again.",
              );
            }
          } finally {
            setLoading(false);
          }
        })}
      >
        <div className="flex flex-col gap-2">
          <InputLabel>
            <H5>Email address</H5>
          </InputLabel>
          <InputContainer>
            <InputEmail
              {...formHook.register("email", { required: true })}
              placeholder={"Email address"}
              autoComplete={"username"}
            />
          </InputContainer>
        </div>
        <div className="flex flex-col gap-2">
          <InputLabel>
            <H5>Password</H5>
          </InputLabel>
          <InputContainer>
            <InputPassword
              {...formHook.register("password", { required: true })}
              placeholder={"Password"}
              autoComplete={"current-password"}
            />
          </InputContainer>
        </div>
        <Button
          className={"w-full"}
          type={"submit"}
          loading={loading}
          disabled={loading}
        >
          <span>Log in</span>
        </Button>
        {showForgottenPassword && (
          <Link
            to={""}
            onClick={() => setStep(SignupFlowStep.ForgotPasswordStep)}
            className={"text-center underline"}
          >
            <p>Forgot password?</p>
          </Link>
        )}
      </form>
    </>
  );
}
